import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import {
  queryChatMessages,
  queryPersonaChats,
} from '@wix/ambassador-legends-persona-v1-chat/http';
import {
  ChatMessage,
  ChatMode,
  PersonaChat,
  Role,
  SortOrder,
} from '@wix/ambassador-legends-persona-v1-chat/types';
import { shouldReportErrorMonitor } from '../../Challenge/helpers/shouldReportErrorMonitor';
import { handleError } from '../../../ErrorHandler/errorHandlerPropsMap';
import { EDUCATION_NAMESPACE } from '../consts';
import { request } from '../../../../services/request';

export const RECENT_CHATS_LIMIT = 10;
// Chats the learner never wrote in are hidden, so pages are read until the
// list is full; the page cap bounds the requests when most chats are empty
const QUERY_CHATS_LIMIT = 20;
const MAX_CHAT_PAGES = 3;
const FIRST_MESSAGES_LIMIT = 10;
// PersonaChat.messages holds only the latest 100 messages, so a shorter list
// is the whole chat while a full one may be missing the first user message
const EMBEDDED_MESSAGES_LIMIT = 100;

export interface RecentChat {
  id: string;
  // First user message; null when the chat has none yet
  title: string | null;
  createdDate: Date | string | null;
}

// Returns null when the query fails, so the UI can tell it apart from "no chats"
export default async function (
  flowAPI: ControllerFlowAPI,
  personaId: string,
): Promise<RecentChat[] | null> {
  try {
    const transaction =
      flowAPI.panoramaClient.transaction('query-recent-chats');

    transaction.start();

    const recentChats: RecentChat[] = [];
    let cursor: string | null | undefined;

    for (let page = 0; page < MAX_CHAT_PAGES; page++) {
      const response = await request({
        flowAPI,
        options: queryPersonaChats({
          namespaceV2: EDUCATION_NAMESPACE,
          personaId,
          // A cursor carries the filter and sort of the first page
          query: cursor
            ? { cursorPaging: { limit: QUERY_CHATS_LIMIT, cursor } }
            : {
                filter: {
                  mode: ChatMode.PUBLISHED,
                },
                sort: [{ fieldName: 'createdDate', order: SortOrder.DESC }],
                cursorPaging: {
                  limit: QUERY_CHATS_LIMIT,
                },
              },
        }),
        errorOptions: {
          errorCodesMap: {
            applicationError: {},
          },
        },
      });

      const chats = response?.data?.chats ?? [];
      recentChats.push(...(await loadWrittenChats(flowAPI, chats)));

      cursor = response?.data?.pagingMetadata?.cursors?.next;
      const hasNext = response?.data?.pagingMetadata?.hasNext && cursor;
      if (recentChats.length >= RECENT_CHATS_LIMIT || !hasNext) {
        break;
      }
    }

    transaction.finish();

    return recentChats.slice(0, RECENT_CHATS_LIMIT);
  } catch (error) {
    const resolvedError = flowAPI.errorHandler.getResolvedError(error);

    handleError({
      error,
      resolvedError,
      context: 'QueryRecentChats',
      preventErrorMonitorReport: !shouldReportErrorMonitor(error),
    });

    return null;
  }
}

// Titles the chats and drops the ones the learner never wrote in
async function loadWrittenChats(
  flowAPI: ControllerFlowAPI,
  chats: PersonaChat[],
): Promise<RecentChat[]> {
  // The embedded `messages` may be missing or truncated (latest 100 only),
  // so fetch the oldest messages unless they hold the whole chat
  const loadedChats = await Promise.all(
    chats.map(async (chat) => {
      const embedded = chat.messages ?? [];
      const messages =
        embedded.length > 0 && embedded.length < EMBEDDED_MESSAGES_LIMIT
          ? embedded
          : await fetchChatMessages(flowAPI, chat.id);
      const title = messages ? getFirstUserMessageText(messages) : null;

      return {
        // Every panel open creates a chat; hide the ones nobody wrote in.
        // A failed message load keeps the chat, untitled
        isEmpty: messages !== null && title === null,
        chat: {
          id: chat.id,
          title,
          createdDate: chat.createdDate ?? null,
        },
      };
    }),
  );

  return loadedChats.filter(({ isEmpty }) => !isEmpty).map(({ chat }) => chat);
}

async function fetchChatMessages(
  flowAPI: ControllerFlowAPI,
  chatId: string,
): Promise<ChatMessage[] | null> {
  try {
    const response = await request({
      flowAPI,
      options: queryChatMessages({
        chatId,
        namespaceV2: EDUCATION_NAMESPACE,
        // Oldest first (the default is newest first), so the first user
        // message is within the page however long the chat is
        query: {
          sort: [{ fieldName: 'createdDate', order: SortOrder.ASC }],
          cursorPaging: { limit: FIRST_MESSAGES_LIMIT },
        },
      }),
      errorOptions: {
        errorCodesMap: { applicationError: {} },
      },
    });

    return response?.data?.chatMessages ?? [];
  } catch (error) {
    // A missing title must not fail the whole list, but it is still reported;
    // null tells the caller the chat may have messages
    const resolvedError = flowAPI.errorHandler.getResolvedError(error);

    handleError({
      error,
      resolvedError,
      context: 'QueryRecentChatsFirstMessages',
      preventErrorMonitorReport: !shouldReportErrorMonitor(error),
    });

    return null;
  }
}

function getFirstUserMessageText(messages: ChatMessage[]): string | null {
  const time = (message: ChatMessage) =>
    message.createdDate ? new Date(message.createdDate).getTime() : 0;

  const firstUserMessage = messages
    .filter((message) => message.role === Role.USER && message.content?.trim())
    .sort((a, b) => time(a) - time(b))[0];

  return firstUserMessage?.content?.trim() ?? null;
}
